// `PendingQueuePage` — the runtime "approve / skip / snooze" surface
// for relances scheduled in the next 7 days (US-165 gap-design).
//
// Gap-design choice (single paragraph for the report):
// The proto has no pending-relances surface; recon (`07-leads-relances.md`
// Open Q 5) explicitly flags this gap. We design a dedicated full-page
// route at `/relances/pending` rather than a tab inside the voice editor:
// (a) the queue is per-USER, not per-voice, so embedding it under
// `/voice/:linkedinAccountId` would force users to pick an account before
// seeing what's about to fire; (b) routing it as a top-level surface lets
// the W5d.2 sidebar dock-point ("relances" NavId is already declared in
// `Sidebar.tsx`) light up naturally; (c) the queue groups relances by
// scheduled-day (Today / Tomorrow / weekday) so users can scan
// chronologically — the API returns a flat list ordered by
// `scheduledFor ASC` and we group client-side. Each row offers
// `Send now` / `Skip` / `Snooze` (1h / 4h / 24h / custom). Realtime
// `relance.cancelled` events invalidate the list immediately so a manual
// reply on LinkedIn (US-164) reflects without manual refresh.

import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useLocale, useT } from '@barel/i18n';
import {
  invalidatePendingRelances,
  useApproveRelance,
  usePendingRelances,
  useRetryRelance,
  useSkipRelance,
  useSnoozeRelance,
  useUserHubChannel,
  type PendingRelanceRow,
} from '@barel/api-client/react';
import { Button, Card, EmptyState, PageHeader, Skeleton, toast } from '@barel/ui';

import { formatDate } from '@/lib/format';

import { RelanceRow } from './RelanceRow';
import { TemperatureAccordion } from './TemperatureAccordion';

interface DayGroup {
  key: string;
  label: string;
  rows: PendingRelanceRow[];
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function groupByDay(
  rows: PendingRelanceRow[],
  intlTag: string,
  todayLabel: string,
  tomorrowLabel: string,
): DayGroup[] {
  const today = startOfDay(new Date()).getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  const groups = new Map<string, DayGroup>();
  for (const row of rows) {
    const scheduled = startOfDay(new Date(row.scheduledFor)).getTime();
    const key = String(scheduled);
    let group = groups.get(key);
    if (!group) {
      let label: string;
      if (scheduled === today) label = todayLabel;
      else if (scheduled === today + oneDay) label = tomorrowLabel;
      else
        label = formatDate(new Date(scheduled), {
          locale: intlTag,
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });
      group = { key, label, rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }

  return Array.from(groups.values()).sort((a, b) => Number(a.key) - Number(b.key));
}

export function PendingQueuePage() {
  const t = useT('relances');
  const locale = useLocale();
  const intlTag = locale === 'fr' ? 'fr-FR' : 'en-US';
  const qc = useQueryClient();

  const query = usePendingRelances();
  const approve = useApproveRelance();
  const retry = useRetryRelance();
  const skip = useSkipRelance();
  const snooze = useSnoozeRelance();

  // Realtime — invalidate the list whenever the server reconciles a relance.
  useUserHubChannel('relance.cancelled', () => {
    void invalidatePendingRelances(qc);
  });
  useUserHubChannel('relance.sent', () => {
    void invalidatePendingRelances(qc);
  });
  useUserHubChannel('relance.scheduled', () => {
    void invalidatePendingRelances(qc);
  });

  const rows = useMemo(() => query.data?.pending ?? [], [query.data]);
  const groups = useMemo(
    () => groupByDay(rows, intlTag, t('queue.groupToday'), t('queue.groupTomorrow')),
    [rows, intlTag, t],
  );

  const isBusy = approve.isPending || retry.isPending || skip.isPending || snooze.isPending;

  const handleApprove = (id: string): void => {
    approve.mutate(
      { id },
      {
        onSuccess: () => toast.success({ title: t('queue.approveSuccess') }),
        onError: () => toast.error({ title: t('queue.actionError') }),
      },
    );
  };
  const handleSkip = (id: string): void => {
    skip.mutate(
      { id },
      {
        onSuccess: () => toast.success({ title: t('queue.skipSuccess') }),
        onError: () => toast.error({ title: t('queue.actionError') }),
      },
    );
  };
  const handleRetry = (id: string): void => {
    retry.mutate(
      { id },
      {
        onSuccess: () => toast.success({ title: t('queue.retrySuccess') }),
        onError: () => toast.error({ title: t('queue.actionError') }),
      },
    );
  };
  const handleSnooze = (id: string, snoozeUntil: string): void => {
    snooze.mutate(
      { id, snoozeUntil },
      {
        onSuccess: () => toast.success({ title: t('queue.snoozeSuccess') }),
        onError: () => toast.error({ title: t('queue.actionError') }),
      },
    );
  };

  return (
    <div data-component="PendingQueuePage" className="flex flex-col gap-6 p-6 lg:p-8">
      <PageHeader title={t('queue.pageTitle')} subtitle={t('queue.pageSubtitle')} />

      {query.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="pending-queue-loading">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      ) : query.isError ? (
        <Card padding="lg" data-testid="pending-queue-error">
          <EmptyState
            tone="error"
            title={t('queue.error.title')}
            description={t('queue.error.body')}
            size="lg"
            action={
              <Button type="button" variant="secondary" onClick={() => void query.refetch()}>
                {t('queue.error.retry')}
              </Button>
            }
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card padding="lg" data-testid="pending-queue-empty">
          <EmptyState
            title={t('queue.empty.title')}
            description={t('queue.empty.body')}
            size="lg"
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-6" data-testid="pending-queue-list">
          {groups.map((group) => (
            <section
              key={group.key}
              aria-label={group.label}
              data-testid={`pending-queue-day-${group.key}`}
            >
              <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-secondary">
                {group.label}
              </h2>
              <div className="flex flex-col gap-3">
                {group.rows.map((row) => (
                  <RelanceRow
                    key={row.id}
                    row={row}
                    onApprove={handleApprove}
                    onRetry={handleRetry}
                    onSkip={handleSkip}
                    onSnooze={handleSnooze}
                    busy={isBusy}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <TemperatureAccordion />
    </div>
  );
}
